import {useEffect, useRef, useCallback, useMemo} from "@wordpress/element";
import {useElements} from "@stripe/react-stripe-js";
import {isEmail} from '@wordpress/url';
import {doShippingUpdate} from '../api-requests';
import {getPage, getSelectedShippingOption, toCartAddress, getShippingOptions} from '@wc-stripe/blocks-utils';

export const useExpressCheckout = (
    {
        name,
        total,
        events,
        onClick,
        lineItems,
        isExpress = true,
        shippingData,
        paymentMethod,
        activePaymentMethod,
        createPaymentMethod
    }) => {
    const elements = useElements();

    const {
        addSuccessResponseData,
        addCreatePaymentMethodData
    } = events;

    const currentData = useRef({total, lineItems, shippingData, elements, response: {}});

    useEffect(() => {
        currentData.current = {
            ...currentData.current,
            total,
            elements,
            lineItems,
            shippingData,
        }
    }, [total, elements, lineItems, shippingData]);

    const onButtonClick = useCallback((event) => {
        const {
            total,
            lineItems,
            shippingData
        } = currentData.current;
        const {
            needsShipping,
            shippingRates
        } = shippingData;
        const data = {};

        const calculatedTotal = lineItems.reduce((total, item) => total + item.amount, 0);

        if (calculatedTotal === total) {
            data.lineItems = lineItems;
        }

        if (isExpress && needsShipping) {
            const options = getShippingOptions(shippingRates, {label: 'displayName'});
            if (options.length) {
                data.shippingRates = options;
            }
        }

        onClick?.();

        event.resolve(data);

    }, [onClick, isExpress]);

    const onConfirm = useCallback(async (event) => {
        const {
            billingDetails,
            shippingAddress = null
        } = event;
        if (shippingAddress) {
            const {
                name,
                address
            } = shippingAddress;
            currentData.current.response.shippingAddress = toCartAddress()({...address, name});
        }
        if (billingDetails) {
            const {
                name,
                email = '',
                phone = '',
                address
            } = billingDetails;
            if (typeof address === 'object' && Object.keys(address).length) {
                currentData.current.response.billingAddress = toCartAddress()({...address, name});
            }
            if (typeof email === 'string' && isEmail(email)) {
                currentData.current.response.billingAddress.email = email;
            }
            if (typeof phone === 'string' && !!phone) {
                currentData.current.response.billingAddress.phone = phone;
            }

        }
        try {
            await createPaymentMethod();
        } catch (error) {
            event.paymentFailed({
                reason: 'fail',
                message: error.message
            });
        }
    }, [createPaymentMethod]);

    const onShippingChange = useCallback(async (event) => {
        const {
            elements,
            shippingData
        } = currentData.current;
        const {
            shippingAddress
        } = shippingData;
        const {
            reject,
            resolve,
            address = null,
            shippingRate = null
        } = event;
        try {
            const data = {};

            if (address) {
                data.address = {
                    city: address.city || '',
                    state: address.state || '',
                    postcode: address.postal_code || '',
                    country: address.country || ''
                }
            } else {
                data.shipping_method = shippingRate.id;
            }
            const response = await doShippingUpdate({
                ...data,
                payment_method: paymentMethod,
                page_id: getPage()
            });
            if (response.code) {
                throw new Error(response.code);
            }
            if (shippingRate) {
                shippingData.setSelectedRates(...getSelectedShippingOption(shippingRate.id));
            }
            if (data.address) {
                shippingData.setShippingAddress({...shippingAddress, ...data.address});
            }
            const {
                totalCents,
                lineItems,
                shippingOptions
            } = response;

            if (totalCents > 0) {
                elements.update({
                    amount: totalCents
                });
            }
            resolve({
                shippingRates: shippingOptions.map(item => ({
                    id: item.id,
                    amount: item.amountCents,
                    displayName: item.label
                })),
                ...(lineItems.reduce((total, item) => total + item.amountCents, 0) === totalCents && {
                    lineItems: lineItems.map(item => ({
                        name: item.label,
                        amount: item.amountCents
                    }))
                })
            });
        } catch (error) {
            reject(error);
        }
    }, [
        paymentMethod
    ]);

    useEffect(() => {
        /**
         * When isExpress is false, the Express Checkout Element is registered as a regular
         * payment method inside the Checkout block (billingAddressRequired is false), so it
         * does not collect billing details itself. The billing_details built from the block's
         * billing address in useCreatePaymentMethod must be kept so Stripe receives it.
         */
        if (isExpress && paymentMethod === activePaymentMethod) {
            const unsubscribe = addCreatePaymentMethodData((data) => {
                delete data.params;
                return data;
            });

            return unsubscribe;
        }
    }, [
        isExpress,
        paymentMethod,
        activePaymentMethod,
        addCreatePaymentMethodData
    ]);

    useEffect(() => {
        if (name === activePaymentMethod) {
            const unsubscribe = addSuccessResponseData((data, {billingAddress, shippingAddress}) => {
                const {response} = currentData.current;
                if (response.billingAddress) {
                    data.meta.billingAddress = {
                        ...billingAddress,
                        ...response.billingAddress
                    }
                }
                if (response.shippingAddress) {
                    data.meta.shippingAddress = {
                        ...shippingAddress,
                        ...response.shippingAddress
                    }
                }
                return data;
            });

            return unsubscribe;
        }
    }, [
        name,
        activePaymentMethod,
        addSuccessResponseData
    ]);


    return {
        onConfirm,
        onButtonClick,
        onShippingChange
    }
}